/* PavCon, LLC Proprietary.All rights reserved 2022 */
import { AppActions, AppSettingState } from 'src/app/shared/store/app.reducer';
import { IState } from '../store/root.store';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { environment } from 'src/environments/environment';
import { SubscriptionManager } from '../managers/subscription.manager';
import { ConveyorAdminActions } from '../store/conveyor-admin.reducer';
import { HttpHelperService } from './http-helper.service';

export enum AppMods {
  RULES = 'rules',
  MONITOR_REVIEW = 'monitor-review',
  UPLOADER = 'uploader',
  ADMIN_PANEL = 'admin-panel',
}
export interface IAppPermission {
  edit: boolean;
  view: boolean;
  msg?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private baseUrl = `${environment.endpoint}common`;
  settings: any;

  constructor(
    private httpHelperService: HttpHelperService,
    private store: Store<IState>,
    public subMgr: SubscriptionManager
  ) {
    this.reloadStore();
  }
  reloadStore() {
    this.subMgr.add(
      this.store
        .pipe(select((state: IState) => state.app.settings))
        .subscribe((appSettings) => {
          if (appSettings.isLoaded && appSettings.list){
            this.settings = appSettings.list;
          }
        })
    );
  }
  async getVersionNumber(): Promise<any> {
    try {
      return await this.httpHelperService.requestPromise(
        'GET',
        `${this.baseUrl}/settingVersion`
      );
    } catch (error) {
      throw error;
    }
  }

  async getNoAuthSettings(): Promise<any> {
    try {
      const settingsData = await this.getVersionNumber();
      if (settingsData && settingsData.response) {
        return { overwrittenLabels: settingsData.response[0].settings.overwritten_labels };
      }
    } catch (error) {
      throw error;
    }
  }

  async reload(appUser = null): Promise<any> {
    return new Promise(async (resolve) => {
      this.updateStore();
      try {
        const result = await this.httpHelperService.requestPromise(
          'GET',
          `${this.baseUrl}/settings`
        );
        if (result.response) {
          let conf = {};
          const data = result.response;
          const globalSetting = data.filter((d) => d.role === 'global')[0].settings;
          if (appUser) {
            const roleSetting = data.filter((d) => d.role === appUser['custom:role']);
            if (roleSetting[0].settings) {
              conf = Object.assign(globalSetting, roleSetting[0].settings);
            }
            this.updateStore(conf);
          } else {
            conf = globalSetting;
          }
          this.updateStore(conf);
          resolve(conf);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        resolve({});
      }
    });
  }

  updateStore(data = null) {
    this.store.dispatch({
      type: AppActions.SET_SETTINGS,
      payload: {
        isLoaded: data ? true : false,
        list: data ? data : [],
      },
    });
  }
  get(key: string): Promise<any> {
    return new Promise((resolve) => {
      if (!key) {
        return '';
      }
      this.reload().then((global) => {
        resolve(global[key]);
      });
    });
  }
  hasConveyorAccess(unitUUID: string) {
    const access =  this.settings?.conveyorAccess ? this.settings?.conveyorAccess.includes(unitUUID) : false;
    this.store.dispatch({
      type: ConveyorAdminActions.SET_ACCESS,
      payload: access
    });
    return access;
  }
  /**
   * DynamoDB Table: cristil-clean-settings-{evironment}
   * role, settings, version_number
   * {
   *    "role":"{ROLE}",
   *    "settings":{
   *        "permissions":{
   *            "{FILE-TYPE}":{
   *                "{module}":{
   *                    "edit":false,
   *                    "view":true
   *                }
   *            }
   *        }
   *    }
   * }
   *
   * @param module rules,monitor-review,uploader,admin-panel
   * @returns
   */
  hasActionPermission(module, appUser): Promise<IAppPermission> {
    return new Promise((resolve) => {
      const fileType = appUser.fileType;
      this.reload(appUser).then((settingsData) => {
        const perms = settingsData.permissions;
        let result = { view: false, edit: false, msg: 'unset file-type' } as IAppPermission;
        if (fileType && perms) {
          if (perms && perms[fileType]) {
            const actionModule = perms[fileType];
            if (actionModule && actionModule[module]) {
              const priviligies = actionModule[module];
              result = priviligies;
            }
          }
        }
        // console.log('< ' + module + '|' + this.appUser.fileType + '|' + this.role + ' >');
        resolve(result);
      });
    });
  }
}
/* PavCon, LLC Proprietary.All rights reserved 2022 */
